import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Plug, Power, RefreshCw, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';
import { useTenant } from '@/hooks/useTenant';
import { useAttention } from '@/hooks/useAttention';
import { useConnectedSources } from '@/hooks/useConnectedSources';
import { usePmoData } from '@/hooks/usePmoData';
import { useWorkspaceData } from '@/hooks/useRoleHomeData';
import { getRoleDisplayName, type AppRole } from '@/lib/permissions';
import { failedSyncs, sourceHealth } from '@/lib/homeSummaries';
import { HomeActions, HomeEmpty, HomePanel, HomeRow, StatRow, StatTile } from './HomeFrame';

interface Props { onNavigate: (view: string, params?: Record<string, string>) => void; }

const DOT: Record<string, string> = { ok: 'bg-success', warn: 'bg-warning', off: 'bg-muted-foreground/50' };
const STATUS_TEXT: Record<string, string> = { ok: 'Connected', warn: 'Needs attention', off: 'Paused' };

/** Admin home: are the data sources syncing, is AI under control, and who is waiting for access. Heading from the shell. */
const WorkspaceHealth = ({ onNavigate }: Props) => {
  const { t, tx, ago } = useLocalization();
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const attention = useAttention();
  const sourcesQuery = useConnectedSources();
  const pmo = usePmoData();
  const w = useWorkspaceData();

  const sources = useMemo(() => sourcesQuery.data ?? [], [sourcesQuery.data]);
  const health = useMemo(() => sourceHealth(sources), [sources]);
  const failures = useMemo(() => failedSyncs(w.logs), [w.logs]);
  const sourceName = useMemo(() => new Map(sources.map((s) => [s.id, s.name])), [sources]);
  const openIntegrations = () => navigate(tenantSlug ? `/${tenantSlug}/integrations` : '/integrations');
  const aiHalted = w.killSwitch?.is_active === true;
  const breachCount = pmo.breaches?.length ?? null;

  if (w.isLoading || sourcesQuery.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label={t('Loading…')}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HomeActions>
        <Button variant="outline" onClick={() => onNavigate('tenantSettings')}>{t('Org Settings')}</Button>
        <Button onClick={openIntegrations}>{t('Manage on Integrations')}</Button>
      </HomeActions>

      <StatRow>
        <StatTile label={t('Data sources')} icon={<Plug className="h-4 w-4" />} value={sourcesQuery.isError ? '—' : `${health.healthy}/${health.total}`}
          tone={health.stale > 0 ? 'warning' : 'default'}
          hint={sourcesQuery.isError ? t('Not recorded') : health.total === 0 ? t('No data sources connected.') : t('{0} need attention, {1} paused', { 0: health.stale, 1: health.paused })}
          onClick={openIntegrations} />
        <StatTile label={t('Failed syncs (24h)')} icon={<RefreshCw className="h-4 w-4" />} value={w.logsFailed ? '—' : failures.length}
          tone={failures.length > 0 ? 'critical' : 'default'} hint={w.logsFailed ? t('Not recorded') : failures.length ? t('see the list below') : t('No sync failed in the last 24 hours.')}
          onClick={openIntegrations} />
        <StatTile label={t('AI outputs')} icon={<ShieldCheck className="h-4 w-4" />} value={aiHalted ? t('Halted') : attention.pendingApprovals}
          tone={aiHalted || (breachCount ?? 0) > 0 ? 'critical' : 'default'}
          hint={aiHalted ? t('The AI kill switch is active.') : `${t('awaiting review')}${breachCount ? ` · ${t('{0} past their review deadline', { 0: breachCount })}` : ''}`}
          onClick={() => onNavigate('hitlWorkbench')} />
        <StatTile label={t('Users')} icon={<Users className="h-4 w-4" />} value={w.memberCount ?? '—'}
          tone={w.requests.length > 0 ? 'warning' : 'default'}
          hint={w.memberCount === null ? t('Not recorded') : t('{0} role requests waiting', { 0: w.requests.length })}
          onClick={() => onNavigate('tenantSettings')} />
      </StatRow>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Data sources')} hint={t('A source needs attention when it is active but has not synced in 48 hours.')} action={<Badge variant="outline">{health.total}</Badge>}>
          {sources.length === 0 ? (
            <HomeEmpty>{sourcesQuery.isError ? t('Data sources could not be loaded.') : t('No data sources connected.')}</HomeEmpty>
          ) : sources.map((s) => (
            <HomeRow key={s.id} onClick={openIntegrations}>
              <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', DOT[s.status])} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{s.name}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{s.lastSyncAt ? t('Synced {0}', { 0: ago(s.lastSyncAt) }) : t('Never synced')}</span>
              </span>
              <Badge variant="outline" className="shrink-0">{t(STATUS_TEXT[s.status])}</Badge>
            </HomeRow>
          ))}
        </HomePanel>

        <HomePanel title={t('AI safety')} action={aiHalted ? <Power className="h-4 w-4 text-destructive" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4 text-success" aria-hidden="true" />}>
          <div className="flex items-center gap-2 text-sm">
            {w.killSwitch === undefined ? <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" /> : aiHalted ? <Power className="h-4 w-4 text-destructive" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />}
            <span>{w.killSwitch === undefined ? t('Kill switch status could not be read.') : aiHalted ? t('AI outputs halted') : t('AI outputs are flowing through human review.')}</span>
          </div>
          {aiHalted && w.killSwitch?.reason && <p className="text-xs text-muted-foreground">{t('Reason:')} {w.killSwitch.reason}</p>}
          <div className="flex items-center justify-between text-sm"><span>{t('AI outputs awaiting review')}</span><span className="font-semibold tabular-nums">{attention.pendingApprovals}</span></div>
          <div className="flex items-center justify-between text-sm"><span>{t('Policy breaches')}</span><span className="font-semibold tabular-nums">{breachCount ?? '—'}</span></div>
          <Button variant="outline" size="sm" onClick={() => onNavigate('aiControls')}>{t('AI Controls')}</Button>
        </HomePanel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel className="lg:col-span-2" title={t('Failed syncs (24h)')} hint={t('Newest first.')} action={<Badge variant="outline">{failures.length}</Badge>}>
          {failures.length === 0 ? (
            <HomeEmpty>{w.logsFailed ? t('Sync history could not be loaded.') : t('No sync failed in the last 24 hours.')}</HomeEmpty>
          ) : failures.slice(0, 6).map((f) => (
            <HomeRow key={f.id} onClick={openIntegrations}>
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{sourceName.get(f.integration_id) ?? t('Unknown Integration')}</span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">{[ago(f.started_at), f.error_message].filter(Boolean).join(' · ')}</span>
              </span>
            </HomeRow>
          ))}
        </HomePanel>

        <HomePanel title={t('Role requests')} hint={t('People waiting for access.')} action={<Badge variant="outline">{w.requests.length}</Badge>}>
          {w.requests.length === 0 ? (
            <HomeEmpty>{w.requestsFailed ? t('Role requests could not be loaded.') : t('No role requests are waiting.')}</HomeEmpty>
          ) : w.requests.slice(0, 5).map((r) => (
            <HomeRow key={r.id} onClick={() => onNavigate('tenantSettings')}>
              <UserPlus className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{tx(getRoleDisplayName(r.requested_role as AppRole))}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{t('requested {0}', { 0: ago(r.created_at) })}</span>
              </span>
            </HomeRow>
          ))}
        </HomePanel>
      </div>
    </div>
  );
};

export default WorkspaceHealth;
